import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import { Loader2, AlertCircle } from "lucide-react";
import { COMPANY } from "@/lib/compliance";
import { setSession, verifyMagicLink } from "@/lib/portalApi";

/**
 * /customer-portal/verify?token=... — terminal page for the magic link.
 *
 * Exchanges the one-shot token for a portal JWT, stores it in
 * localStorage, then redirects to the dashboard. Shows a clear error if
 * the link is expired or invalid.
 */
export default function CustomerPortalVerifyPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("verifying"); // verifying | error
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Verifying sign-in — Endurance Casualty";
        const token = (params.get("token") || "").trim();
        if (!token) {
            setStatus("error");
            setError("This sign-in link is missing its token. Please request a new link.");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await verifyMagicLink(token);
                if (cancelled) return;
                setSession({ token: data.token, user: data.user });
                navigate("/customer-portal/dashboard", { replace: true });
            } catch (e) {
                if (cancelled) return;
                setStatus("error");
                const detail = e?.response?.data?.detail;
                // FastAPI returns a string for HTTPException but an array of
                // ValidationError objects for Pydantic 422. Coerce to a friendly
                // string so the user always sees a clear message.
                const msg =
                    typeof detail === "string"
                        ? detail
                        : "This sign-in link is invalid or expired. Please request a new one.";
                setError(msg);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [params, navigate]);

    return (
        <main data-testid="page-portal-verify" className="min-h-screen bg-brand-bg text-brand-ivory">
            <PageHeader />
            <section className="mx-auto max-w-2xl px-6 py-24 md:px-10">
                <div className="rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-8 md:p-10">
                    {status === "verifying" && (
                        <div className="flex flex-col items-center gap-4 text-center">
                            <Loader2
                                aria-hidden="true"
                                className="h-10 w-10 animate-spin text-brand-gold2"
                            />
                            <p className="font-serif text-2xl text-brand-ivory">
                                Verifying your sign-in link…
                            </p>
                            <p className="text-sm text-brand-muted">
                                One moment while we set up your secure
                                session.
                            </p>
                        </div>
                    )}
                    {status === "error" && (
                        <div data-testid="portal-verify-error">
                            <div className="flex items-start gap-3">
                                <AlertCircle
                                    aria-hidden="true"
                                    className="mt-0.5 h-6 w-6 shrink-0 text-red-300"
                                />
                                <div>
                                    <p className="font-serif text-2xl text-brand-ivory">
                                        Sign-in link couldn&apos;t be used
                                    </p>
                                    <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                        {error}
                                    </p>
                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <Link
                                            to="/customer-portal/login"
                                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg transition-colors hover:bg-brand-gold2"
                                        >
                                            Request a new link
                                        </Link>
                                        <a
                                            href={`tel:${COMPANY.phoneTel}`}
                                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-brand-gold2"
                                        >
                                            Call {COMPANY.phone}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <FooterBar />
        </main>
    );
}
